import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** 主题类型 */
export type Theme = 'light' | 'dark' | 'system'

/** 解析后的主题（实际应用的主题） */
export type ResolvedTheme = 'light' | 'dark'

/**
 * UI 状态类型
 */
interface UIState {
  /** 侧边栏是否折叠 */
  sidebarCollapsed: boolean
  /** 主题模式 */
  theme: Theme
  /** 系统主题偏好 */
  systemTheme: ResolvedTheme
  /** 切换侧边栏 */
  toggleSidebar: () => void
  /** 设置侧边栏状态 */
  setSidebarCollapsed: (collapsed: boolean) => void
  /** 设置主题 */
  setTheme: (theme: Theme) => void
  /** 切换主题（在 light/dark 之间循环） */
  toggleTheme: () => void
  /** 设置系统主题（内部使用） */
  setSystemTheme: (theme: ResolvedTheme) => void
  /** 获取解析后的主题 */
  getResolvedTheme: () => ResolvedTheme
}

/**
 * 获取当前系统主题
 */
function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') {return 'light'}
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * UI 状态 Store
 * - 仅用于 UI 状态（侧边栏、主题等）
 * - 服务器状态由 TanStack Query 管理
 */
export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      theme: 'system',
      systemTheme: getSystemTheme(),

      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),

      setTheme: (theme) => {
        set({ theme })
        // 应用主题到 DOM
        applyTheme(theme, get().systemTheme)
      },

      toggleTheme: () => {
        const currentResolved = get().getResolvedTheme()
        const newTheme: Theme = currentResolved === 'light' ? 'dark' : 'light'
        set({ theme: newTheme })
        applyTheme(newTheme, get().systemTheme)
      },

      setSystemTheme: (systemTheme) => {
        set({ systemTheme })
        // 如果当前是系统主题模式，需要更新 DOM
        if (get().theme === 'system') {
          applyTheme('system', systemTheme)
        }
      },

      getResolvedTheme: () => {
        const { theme, systemTheme } = get()
        return theme === 'system' ? systemTheme : theme
      },
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
    }
  )
)

/**
 * 应用主题到 DOM
 */
function applyTheme(theme: Theme, systemTheme: ResolvedTheme) {
  const root = document.documentElement
  const resolvedTheme = theme === 'system' ? systemTheme : theme
  root.classList.toggle('dark', resolvedTheme === 'dark')
}

/**
 * 初始化系统主题监听
 */
function initSystemThemeListener() {
  if (typeof window === 'undefined') {return}

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  
  const handleChange = (e: MediaQueryListEvent) => {
    const newSystemTheme: ResolvedTheme = e.matches ? 'dark' : 'light'
    useUIStore.getState().setSystemTheme(newSystemTheme)
  }

  // 使用 addEventListener 而不是 addListener（已废弃）
  mediaQuery.addEventListener('change', handleChange)
}

// 初始化时应用主题
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('ui-storage')
  const systemTheme = getSystemTheme()
  
  if (stored) {
    try {
      const { state } = JSON.parse(stored) as { state: { theme: Theme } }
      applyTheme(state.theme, systemTheme)
    } catch {
      applyTheme('system', systemTheme)
    }
  } else {
    applyTheme('system', systemTheme)
  }

  // 初始化系统主题监听
  initSystemThemeListener()
}
